import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { toJpeg } from 'html-to-image';
import { Bug, X, Send, Eraser, Loader2 } from 'lucide-react';
import { APP_INFO, getDeviceId } from '../lib/license';
import { modeId } from '../lib/profiles';
import { portalRoot } from './TourGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/feedback`;
// screen shot of the whole app (map tiles that block copying are left blank), max 1400 px wide
async function shot() {
  const el = document.body, W = window.innerWidth, H = window.innerHeight, w = Math.min(1, (W < 700 ? 900 : 1400) / W);
  return toJpeg(el, { quality: 0.7, pixelRatio: w, width: W, height: H, style: { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)` }, cacheBust: false, skipFonts: true, imagePlaceholder: 'data:image/gif;base64,R0lGODlhAQABAAAAACw=', filter: (n) => !n.dataset?.noShot });
}

function Sketch({ src, canvasRef }) {
  const draw = useRef(false);
  useEffect(() => {
    const c = canvasRef.current, img = new Image();
    img.onload = () => { c.width = img.width; c.height = img.height; c.getContext('2d').drawImage(img, 0, 0); };
    img.src = src;
  }, [src, canvasRef]);
  const pos = (e) => { const c = canvasRef.current, r = c.getBoundingClientRect(), p = e.touches?.[0] || e; return [((p.clientX - r.left) * c.width) / r.width, ((p.clientY - r.top) * c.height) / r.height]; };
  const down = (e) => { draw.current = true; const g = canvasRef.current.getContext('2d'), [x, y] = pos(e); g.strokeStyle = '#ef4444'; g.lineWidth = Math.max(3, canvasRef.current.width / 250); g.lineCap = 'round'; g.beginPath(); g.moveTo(x, y); };
  const move = (e) => { if (!draw.current) return; e.preventDefault(); const g = canvasRef.current.getContext('2d'), [x, y] = pos(e); g.lineTo(x, y); g.stroke(); };
  return <canvas ref={canvasRef} className="fb-canvas" data-testid="feedback-canvas" onPointerDown={down} onPointerMove={move} onPointerUp={() => { draw.current = false; }} onPointerLeave={() => { draw.current = false; }} />;
}

// tester bug report: automatic screen shot (draw on it), text, send to the owner
export default function FeedbackButton({ t }) {
  const [img, setImg] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const cv = useRef(null);
  const start = async () => {
    setBusy(true);
    try { setImg(await shot()); } catch { setImg(null); }
    setBusy(false); setOpen(true);
  };
  const send = async () => {
    setBusy(true);
    try {
      const image = img && cv.current ? cv.current.toDataURL('image/jpeg', 0.75) : null;
      const r = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ device_id: getDeviceId(), text, lang: document.documentElement.lang || 'de', mode: modeId(), screen: window.location.hash || window.location.pathname, version: APP_INFO.version, ua: navigator.userAgent.slice(0, 300), image }) });
      if (!r.ok) throw new Error(String(r.status));
      toast.success(t.fbThanks); setOpen(false); setText(''); setImg(null);
    } catch { toast.error(t.fbFail); }
    setBusy(false);
  };
  return (
    <>
      <button className="fb-fab" data-no-shot="1" data-testid="feedback-btn" title={t.fbTitle} disabled={busy} onClick={start}>{busy && !open ? <Loader2 size={18} className="spin" /> : <Bug size={18} />}</button>
      {open && createPortal(
        <div className="editor-backdrop" data-no-shot="1" data-testid="feedback-dialog">
          <div className="editor fb-dialog">
            <div className="editor-head"><h4><Bug size={18} /> {t.fbTitle}</h4><button className="icon-btn" data-testid="feedback-close-btn" onClick={() => setOpen(false)}><X size={16} /></button></div>
            {img ? <><Sketch src={img} canvasRef={cv} /><div className="row"><small className="muted">{t.fbDrawHint}</small><button className="btn btn-outline btn-sm" data-testid="feedback-clear-btn" onClick={() => { const i = img; setImg(null); setTimeout(() => setImg(i), 0); }}><Eraser size={14} /> {t.fbClear}</button><button className="btn btn-outline btn-sm" data-testid="feedback-noimg-btn" onClick={() => setImg(null)}><X size={14} /> {t.fbNoImg}</button></div></>
              : <p className="muted small-hint" data-testid="feedback-noimg">{t.fbNoShot}</p>}
            <textarea data-testid="feedback-text" rows={4} value={text} placeholder={t.fbPh} onChange={(e) => setText(e.target.value)} />
            <button className="btn btn-green" data-testid="feedback-send-btn" disabled={busy || text.trim().length < 3} onClick={send}>{busy ? <Loader2 size={16} className="spin" /> : <Send size={16} />} {t.fbSend}</button>
            <p className="muted small-hint">{t.fbPrivacy}</p>
          </div>
        </div>,
        portalRoot(),
      )}
    </>
  );
}
